import Checkbox from '@amzn/awsui-components-react-v3/polaris/checkbox';
import { Button, ColumnLayout } from 'AWS-UI-Components-React';
import React from 'react';
import { ReferenceLine } from 'recharts';
import {
  ASIN_METRIC_TYPE,
  ASIN_TYPE,
  ASIN_TYPE_DROPDOWN_ELEMENTS,
  ASIN_TYPE_NAMES,
  COMPONENT_NAMES,
  STABILIZATION,
  TREND_CHART_DROPDOWN_ELEMENTS,
} from '../../../constants/asin_metric_elements';
import { CHANNELS } from '../../../constants/channels';
import { CartesianGrid, ComposedChart, Label, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from '../../../recharts';
import { AccuracyButtonNewStyle, ASINChartStyle } from '../../../styles/common_styles';
import { downloadFile, toDownloadFormat } from '../../../utilities';
import { formatTimeFrameToYYYYMM, formatYYYYMMtoTimeFrame, unixToFormattedDate } from '../../../utils/date-utils';
import { CustomizedDot, getMaximallyDifferentRandomColor } from '../../../utils/graph-utils';
import { TrendFilter } from '../../buttons/Trend_Filter';
import { EmptyMessage } from '../../content/empty_message';
import { ErrorFlash } from '../../content/error_flash';
import { LoadingSpinner } from '../../content/loading_spinner';
import { OPERATION_STATUS } from '../common/constants';
import { getAsinMetricsClient } from './asin_metrics_client';
import {
  extractAsinType,
  extractComponent,
  extractMetric,
  extractMonth,
  filterLinesOrBars,
  generateLabel,
  getColumnDefinitions,
  getStabilizationUnixTimestamp,
} from './asin_metrics_utils';
import { CompareMonthWindow } from './compare_month_window';
import CustomTooltipContent from './stabilization_custom_tooltip_content';

export class ASINTrendChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      stabilizationPeriodLoadStatus: OPERATION_STATUS.NONE,
      selectedComponents: this.props.component ? [this.props.component] : ['contributionProfit', 'contributionProfitWithoutRefunds'],
      selectedMetrics: [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT],
      selectedMonths: this.props.availableMonths,
      compareStatus: OPERATION_STATUS.NONE,
      downloadStatus: OPERATION_STATUS.NONE,
      displayStabilizationPeriod: false,
      selectedAsinTypes: [ASIN_TYPE.ALL],
    };

    this.stabilizationPeriod = {};
  };

  componentDidMount = () => {
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.asinMetricsClient = getAsinMetricsClient();
    const stabilizationPeriodPayload = this.prepareStabilizationPeriodPayload();
    this.pullStabilizationPeriod(stabilizationPeriodPayload);
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.availableMonths !== this.props.availableMonths) {
      const selectedMonths = [...new Set([...this.state.selectedMonths, ...this.props.availableMonths])];
      this.setState({selectedMonths});
    }
  };

  prepareStabilizationPeriodPayload = () => {
    let channel = this.props.payload.channel;
    if(channel === CHANNELS.RETAIL) channel = 'Retail'; //Backend only accepts Retail not RETAIL
    let payload = Object.assign({}, {
      channel, gls: this.props.payload.gls,
      marketplaceId: this.props.payload.marketplace,
      metrics: [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT],
    });
    if(this.props.payload.channelType){
      payload.channelType = parseInt(this.props.payload.channelType);
    }
    return payload;
  }

  pullStabilizationPeriod = (payload) => {
    this.asinMetricsClient.getStabilizationPeriod(this.processStabilizationPeriod, this.onFailedRequest, payload);

  }
  processStabilizationPeriod = (response) => {
    if(response && response.data && response.data.stabilizationPeriodByMetric){
      const stabilizationPeriodByMetric = response.data.stabilizationPeriodByMetric;
      for(const metric of Object.keys(stabilizationPeriodByMetric)) {
        this.stabilizationPeriod[metric] = stabilizationPeriodByMetric[metric].stabilizationPeriodByComponent;
      }
    }
    this.setState({stabilizationPeriodLoadStatus: OPERATION_STATUS.SUCESS});
  }

  onFailedRequest = () => {
    this.setState({stabilizationPeriodLoadStatus: OPERATION_STATUS.FAIL});
  };

  getLines = (filteredLines) => {
    let lines = [];
    let colorIndex = 0;
    const {selectedMetrics, selectedMonths, selectedAsinTypes} = this.state;
    const stabilizationPeriodPayloads = this.getStabilizationPeriodPayloads(filteredLines);
    filteredLines.forEach((id) => {
      const color = getMaximallyDifferentRandomColor(colorIndex++, filteredLines.length);
      lines.push(
        <Line
          name={ generateLabel(id, selectedMonths.length > 1,
                               selectedMetrics.length > 1, selectedAsinTypes.length > 1) }
          strokeWidth="2"
          key={ id }
          dataKey={ id }
          dot={ <CustomizedDot /> }
          connectNulls={ true }
          stroke={ color } />);

      const stabilizationPayload = stabilizationPeriodPayloads.find(payload => payload.id === id);
      if(typeof stabilizationPayload !== 'undefined'){
        lines.push(this.getStabilizationPeriodReferenceLine(stabilizationPayload.id, stabilizationPayload.stabilizationDate, color));
      }
    });
    return lines;
  };

  getStabilizationPeriodPayloads = (filteredLines) => {
    const {displayStabilizationPeriod} = this.state;
    let stabilizationPayloads = [];
    if(displayStabilizationPeriod){
      filteredLines.filter(id => ASIN_TYPE.ALL === extractAsinType(id))
                   .forEach(id => {
                     const stabilizationPeriod = this.getStabilizationPeriodInDays(id);
                     if(typeof stabilizationPeriod !== 'undefined') {
                       const month = extractMonth(id);
                       const stabilizationDate = getStabilizationUnixTimestamp(month, stabilizationPeriod);
                       stabilizationPayloads.push({id, stabilizationDate, stabilizationPeriod});
                     }
                   })
    }
    return stabilizationPayloads;
  }

  getStabilizationPeriodReferenceLine = (compositeKey, stabilizationDate, color) => {
    return (
      <ReferenceLine
        strokeWidth="2"
        key={ `${compositeKey}-${STABILIZATION}` }
        x={ stabilizationDate }
        stroke={ color}
        strokeDasharray="3 3"
        isFront
        ifOverflow="extendDomain"
      />);
  }

  getStabilizationPeriodInDays = (compositeKey) => {
    const metric = extractMetric(compositeKey);
    const component = extractComponent(compositeKey);
    if(this.stabilizationPeriod[metric]){
      return this.stabilizationPeriod[metric][component];
    }
  }

  getComponentDropdownElements = () => {
    return Object.keys(COMPONENT_NAMES).map(comp => { return {value: comp, label:COMPONENT_NAMES[comp]};});
  };

  setSelectedMetrics = (chosen) => {
    this.setState({selectedMetrics: chosen.detail.selectedOptions.map(o => o.value)});
  };

  setSelectedComponents = (chosen) => {
    this.setState({selectedComponents: chosen.detail.selectedOptions.map(o => o.value)});
  };

  setSelectedMonths = (chosen) => {
    this.setState({selectedMonths: chosen.detail.selectedOptions.map(o => o.value)});
  };

  setSelectedAsinTypes = (chosen) => {
    this.setState({selectedAsinTypes: chosen.detail.selectedOptions.map(o => o.value)});
  };

  getFormattedChartData = (data, displayInMillions) => {
    let newData = [];
    for(const snapshotRow of data){
      let newSnapshotRow = Object.assign({}, snapshotRow);
      for(const key of Object.keys(snapshotRow)){
        if(key !== 'snapshot'){
          const metric = extractMetric(key);
          newSnapshotRow[key] = this.getFormattedValue(snapshotRow[key], metric, displayInMillions);
        }
      }
      newData.push(newSnapshotRow);
    }
    //sort data rows based on snapshot date;
    newData.sort((rowA, rowB) => rowA.snapshot - rowB.snapshot);
    return newData;
  };

  getFormattedValue = (value, metric, displayInMillions) => {
    let formattedValue;
    if([ASIN_METRIC_TYPE.NET_VAR_PER_UNIT,
        ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT,
        ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT,
        ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT].includes(metric)){
      formattedValue = value;
    } else {
      formattedValue = displayInMillions ? value/1000000: value;
    }
    return formattedValue.toFixed(2);
  };

  getMonthsDropdownElements = () => {
    return this.props.availableMonths.map(m => {return {value: m, label: m};});
  };

  onCompareMonth = () => {
    this.setState({compareStatus: OPERATION_STATUS.IN_PROGRESS});
  };

  onCancelCompare = () => {
    this.setState({compareStatus: OPERATION_STATUS.NONE});
  };

  onSubmitCompare = (timeFrames) => {
    this.setState({compareStatus: OPERATION_STATUS.SUCESS});
    this.props.onCompareMonths(timeFrames);
  };

  /**
   * Exclude months that have already been pulled from Compare Months dropdown
   */
  getExcludedTimeFrames = () => {
    return this.props.availableMonths.map(m => formatYYYYMMtoTimeFrame(m));
  };

  getDataMinandMax = (filteredLines) => {
    const {data, displayInMillions} = this.props;
    const filteredData = data.map(snapshot => Object.keys(snapshot)
                                                    .filter(key => filteredLines.includes(key))
                                                    .map(key => this.getFormattedValue(snapshot[key],
                                                                                       extractMetric(key), displayInMillions)))
                             .flat();
    const dataMin = filteredData.length > 0 ? Math.min(...filteredData) : 0;
    const dataMax = filteredData.length > 0 ? Math.max(...filteredData) : 0;
    return [dataMin, dataMax];
  }

  getDateMinandMax = (data) => {
    const timestamps = data.map(snapshotRow => snapshotRow.snapshot);
    const dateMin = timestamps.length > 0 ? Math.min(...timestamps) : 0;
    const dateMax = timestamps.length > 0 ? Math.max(...timestamps) : 0;
    return [dateMin, dateMax];
  }

  /** Convert snapshotToComponentMetricsMap (snapshot => Map(component => Map(metric=> metric value)))
   * to desired excel download format.
   */
  onDownloadData = () => {
    const {metricsMap, displayInMillions, payload} = this.props;
    this.setState({downloadStatus: OPERATION_STATUS.IN_PROGRESS});
    const month = formatTimeFrameToYYYYMM(payload.timeFrame);

    let dataRows = [];
    for( const [asinType, snapshotToComponentMetricMap] of metricsMap.entries()){
      for (const [snapshot, componentToMetricsMap] of snapshotToComponentMetricMap.entries()){
        for (const [component, metricsMap] of componentToMetricsMap.entries()){
          const componentName = COMPONENT_NAMES[component] || component;
          dataRows.push({component: componentName,
                          snapshot: snapshot,
                          asinType: ASIN_TYPE_NAMES[asinType],
                          month: month,
                          [ASIN_METRIC_TYPE.NET_FA]: metricsMap.get(ASIN_METRIC_TYPE.NET_FA),
                          [ASIN_METRIC_TYPE.NET_LA]: metricsMap.get(ASIN_METRIC_TYPE.NET_LA),
                          [ASIN_METRIC_TYPE.FA_UNITS]: metricsMap.get(ASIN_METRIC_TYPE.FA_UNITS),
                          [ASIN_METRIC_TYPE.LA_UNITS]: metricsMap.get(ASIN_METRIC_TYPE.LA_UNITS),
                          [ASIN_METRIC_TYPE.NET_VAR_PER_UNIT]: metricsMap.get(ASIN_METRIC_TYPE.NET_VAR_PER_UNIT),
                          [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT]: metricsMap.get(ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT),
                          [ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT]: metricsMap.get(ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT),
                          [ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT]: metricsMap.get(ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT)
                        });
        }
      }
    }
    const data = toDownloadFormat(getColumnDefinitions(dataRows, displayInMillions), dataRows);
    downloadFile(data, `${this.props.metricName}_Trend_${month}.xls`);
    this.setState({downloadStatus: OPERATION_STATUS.SUCESS});
  };

  render() {
    const {data, lines, displayInMillions, loading} = this.props;
    const {selectedComponents, selectedMetrics, selectedMonths, selectedAsinTypes} = this.state;
    const componentOpts = this.getComponentDropdownElements();
    const monthOpts = this.getMonthsDropdownElements(data);
    const empty = !data || !data.length;
    const enableCompareMonths = this.props.hasOwnProperty('enableCompareMonths') ? this.props.enableCompareMonths : false;
    const enableStabilization = this.props.hasOwnProperty('enableStabilization') ? this.props.enableStabilization : false;
    const filteredLines = filterLinesOrBars(lines, selectedComponents, selectedMetrics, selectedMonths, selectedAsinTypes);
    const [dataMin, dataMax] = this.getDataMinandMax(filteredLines);
    const [dateMin, dateMax] = this.getDateMinandMax(data);

    return (
      <div className={ASINChartStyle}>
        {this.state.stabilizationPeriodLoadStatus === OPERATION_STATUS.FAIL && (<ErrorFlash type="Stabilization Period" />)}
        {this.state.compareStatus === OPERATION_STATUS.IN_PROGRESS && (
         <CompareMonthWindow onCancel={this.onCancelCompare} onSubmit={this.onSubmitCompare} exclude={this.getExcludedTimeFrames(data)}/>
        )}
        <div className="awsui-util-container">
          <div className="awsui-util-container-header">
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <div className="awsui-util-font-size-2">
                  { `${this.props.metricName} Trend` }
                </div>
              </div>
              <div className="awsui-util-action-stripe-group">
                <Button
                  text="Download"
                  variant="normal"
                  onClick={this.onDownloadData}
                  disabled={loading || empty}
                  loading={this.state.downloadStatus === OPERATION_STATUS.IN_PROGRESS}
                  data-cy="asin-trend-download"
                />
                {enableCompareMonths && (<Button
                    text="Compare Months"
                    variant="normal"
                    onClick={this.onCompareMonth}
                    disabled={loading || empty}
                    loading={this.state.compareStatus === OPERATION_STATUS.IN_PROGRESS}
                    data-cy="compare-button"
                  />
                )}
              </div>
            </div>
          </div>
          {!loading && !empty && (
            <div>
              <ColumnLayout columns={4}>
                <div data-awsui-column-layout-root="true">
                  <div>
                    <TrendFilter
                      text="Component"
                      classname={AccuracyButtonNewStyle}
                      options={componentOpts}
                      onChange={this.setSelectedComponents}
                      selectedIds={this.state.selectedComponents}
                    />
                  </div>
                  <div>
                    <TrendFilter
                      text={this.props.metricName}
                      classname='AccuracyTypeMultiSelect'
                      options={TREND_CHART_DROPDOWN_ELEMENTS}
                      onChange={this.setSelectedMetrics}
                      selectedIds={this.state.selectedMetrics}
                    />
                  </div>
                  <div>
                    <TrendFilter
                      text="Month"
                      classname={AccuracyButtonNewStyle}
                      options={monthOpts}
                      onChange={this.setSelectedMonths}
                      selectedIds={this.state.selectedMonths}
                    />
                  </div>
                  <div>
                    <TrendFilter
                      text="ASIN Type"
                      classname='AccuracyTypeMultiSelect'
                      options={ASIN_TYPE_DROPDOWN_ELEMENTS}
                      onChange={this.setSelectedAsinTypes}
                      selectedIds={selectedAsinTypes}
                    />
                  </div>
                </div>
              </ColumnLayout>
              {enableStabilization && ( <div className="awsui-util-f-r" style={{marginRight: '10px'}}>
                  <Checkbox
                    onChange={({ detail }) => this.setState({displayStabilizationPeriod: detail.checked})}
                    checked={this.state.displayStabilizationPeriod}
                  >
                    Display Stabilization Period
                  </Checkbox>
              </div> )}
              <div style={{ width: '99%', padding: '0'}}>
                <ResponsiveContainer aspect={3}>
                  <ComposedChart data={this.getFormattedChartData(data, displayInMillions)}
                                 margin={{ top: 10, right: 50, left: 40, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" dataKey='snapshot' scale='utc' label={{ value: 'As of Date', position: 'insideBottomLeft',
                      dx: -10, dy: 10}} domain={[dateMin, dateMax]} tickFormatter={unixToFormattedDate} />
                    <YAxis type="number" domain={[dataMin, dataMax]} tickFormatter={(val) => val.toFixed(2)} >
                      <Label value='Amount ($)' position='insideLeft' angle={-90}/>
                    </YAxis>
                    <Tooltip labelFormatter={unixToFormattedDate}
                             content={<CustomTooltipContent stabilizationPayloads={this.getStabilizationPeriodPayloads(filteredLines)}
                                                            selectedMetrics={selectedMetrics}
                                                            selectedMonths={selectedMonths}
                                                            selectedAsinTypes={selectedAsinTypes}
                             />}/>
                    <Legend wrapperStyle={{paddingTop: '20px'}}/>
                    {this.getLines(filteredLines)}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>)}
          {loading && (<LoadingSpinner />)}
          {!loading && empty && (<EmptyMessage />)}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/asin_trend_chart.jsx